import { useContext, useReducer } from 'react';
import { QueryHookOptions } from '@apollo/react-hooks';
import {
  AccountDetail,
  Scalars,
  NotificationFor,
} from '../../../__generated__/pge-types';
import { getAccountsList } from '../../account-dropdown/queries';
import { NotificationsContext } from '../../../providers/NotificationsProvider';
import { useTranslation } from '../../../hooks/useTranslation';
import useAccountDetailList from '../../../hooks/useAccountDetailList';
import AccountListReducer from '../../account-summary/multi-accounts/accountListReducer';
import useAuthQuery from '../../../hooks/useAuthQuery';
import useFeatureFlagProtectedValues from '../../account-summary/multi-accounts/useFeatureFlagProtectedValues';
import { VirtualAccountType } from '../../../constants/customEnum';
import { getAccountDetails } from '../queries/queries';

type AccountIdentifier = {
  accountNumber?: Scalars['String'];
  encryptedPersonId?: Scalars['String'];
};

type AccountIdentifierList = {
  validAccounts: Array<AccountIdentifier>;
};

export const useGetUserAccounts = () => {
  const { t } = useTranslation();

  const notificationContext = useContext(NotificationsContext);
  const setErrorNotification = (isError: boolean) => {
    notificationContext.setState({
      isOpen: isError,
      message: isError
        ? t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY')
        : undefined,
      severity: isError ? 'error' : undefined,
    });
    isError && typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const {
    getAccountListStateByClosedAcctFlag,
  } = useFeatureFlagProtectedValues();

  const [accountListParams] = useReducer(AccountListReducer.reducer, {
    ...getAccountListStateByClosedAcctFlag(),
    pageSize: 'All',
  });

  const { accounts } = useAccountDetailList(
    getAccountsList,
    VirtualAccountType.ALL_ACCTS,
    {
      accountListParams,
    },
  );

  const { validAccounts } = accounts.reduce(
    (acc: AccountIdentifierList, account) => {
      if (account.isActive) {
        if (account.isDefault) {
          acc.validAccounts.push({
            accountNumber: account.accountNumber,
            encryptedPersonId: account.encryptedPersonId,
          });
        } else if (acc.validAccounts.length < 5 && !account.isDefault) {
          acc.validAccounts.push({
            accountNumber: account.accountNumber,
            encryptedPersonId: account.encryptedPersonId,
          });
        }
      }
      return acc;
    },
    { validAccounts: [] },
  );

  const accountParams = JSON.parse(JSON.stringify(validAccounts));

  const { loading, data: accountDetails, refetch } = useAuthQuery<{
    getAccountDetails: Array<AccountDetail>;
  }>(getAccountDetails, {
    variables: {
      params: {
        accountNumberList: accountParams,
      },
      commPreferencesParams: {
        notificationFor: NotificationFor.Outage,
      },
    },
    skip: !accountParams,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    onError: () => {
      setErrorNotification(true);
    },
  });

  return {
    accountDetails: accountDetails?.getAccountDetails,
    loading,
    refetch,
  };
};
